import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  ArrowLeft,
  Hammer,
  Plus,
  FileText,
  Receipt,
  Pencil,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Home,
  Building,
  Bath,
  UtensilsCrossed,
  Paintbrush,
  Wrench,
  TreePine,
  Save,
  Eye,
  DollarSign,
  Clock,
  Users,
  Ruler
} from 'lucide-react';
import { toast } from 'sonner';

// Construction Project Types with their default fields
const constructionProjectTypes = [
  {
    id: 'home_renovation',
    name: 'Home Renovation',
    icon: Home,
    color: 'amber',
    description: 'Full home renovation and remodeling projects',
    fields: [
      { name: 'property_type', label: 'Property Type', type: 'select', options: ['Single Family', 'Townhouse', 'Condo', 'Multi-Family', 'Commercial'], required: true },
      { name: 'square_footage', label: 'Total Square Footage', type: 'number', required: true },
      { name: 'rooms_affected', label: 'Rooms to Renovate', type: 'textarea', required: true },
      { name: 'structural_changes', label: 'Structural Changes Required', type: 'checkbox', required: false },
      { name: 'permit_required', label: 'Permits Required', type: 'checkbox', required: false },
      { name: 'hvac_update', label: 'HVAC Updates', type: 'checkbox', required: false },
      { name: 'electrical_update', label: 'Electrical Updates', type: 'checkbox', required: false },
      { name: 'plumbing_update', label: 'Plumbing Updates', type: 'checkbox', required: false },
      { name: 'timeline_weeks', label: 'Estimated Timeline (weeks)', type: 'number', required: false },
      { name: 'occupancy_during', label: 'Client Living On-Site During Work', type: 'checkbox', required: false },
      { name: 'material_grade', label: 'Material Grade', type: 'select', options: ['Standard', 'Premium', 'Luxury'], required: false },
      { name: 'special_requirements', label: 'Special Requirements', type: 'textarea', required: false }
    ]
  },
  {
    id: 'new_construction',
    name: 'New Construction',
    icon: Building,
    color: 'blue',
    description: 'Ground-up construction projects',
    fields: [
      { name: 'building_type', label: 'Building Type', type: 'select', options: ['Residential', 'Commercial', 'Industrial', 'Mixed-Use'], required: true },
      { name: 'lot_size', label: 'Lot Size (sq ft)', type: 'number', required: true },
      { name: 'building_sqft', label: 'Building Square Footage', type: 'number', required: true },
      { name: 'stories', label: 'Number of Stories', type: 'number', required: true },
      { name: 'bedrooms', label: 'Bedrooms', type: 'number', required: false },
      { name: 'bathrooms', label: 'Bathrooms', type: 'number', required: false },
      { name: 'garage', label: 'Garage Type', type: 'select', options: ['None', 'Attached 1-Car', 'Attached 2-Car', 'Attached 3-Car', 'Detached'], required: false },
      { name: 'foundation_type', label: 'Foundation Type', type: 'select', options: ['Slab', 'Crawl Space', 'Basement', 'Pier & Beam'], required: true },
      { name: 'architectural_plans', label: 'Architectural Plans Provided', type: 'checkbox', required: false },
      { name: 'site_prep_needed', label: 'Site Preparation Required', type: 'checkbox', required: false },
      { name: 'utility_connections', label: 'Utility Connections Needed', type: 'checkbox', required: false },
      { name: 'energy_rating', label: 'Energy Efficiency Target', type: 'select', options: ['Standard Code', 'Energy Star', 'LEED Certified', 'Net Zero'], required: false },
      { name: 'estimated_completion', label: 'Target Completion Date', type: 'date', required: false },
      { name: 'budget_range', label: 'Budget Range', type: 'select', options: ['Under $200K', '$200K-$400K', '$400K-$600K', '$600K-$1M', 'Over $1M'], required: false }
    ]
  },
  {
    id: 'kitchen_remodel',
    name: 'Kitchen Remodel',
    icon: UtensilsCrossed,
    color: 'green',
    description: 'Kitchen renovation and upgrades',
    fields: [
      { name: 'kitchen_size', label: 'Kitchen Size (sq ft)', type: 'number', required: true },
      { name: 'layout_change', label: 'Layout Changes', type: 'checkbox', required: false },
      { name: 'cabinet_type', label: 'Cabinet Type', type: 'select', options: ['Stock', 'Semi-Custom', 'Custom', 'Refinish Existing'], required: true },
      { name: 'countertop_material', label: 'Countertop Material', type: 'select', options: ['Laminate', 'Granite', 'Quartz', 'Marble', 'Butcher Block', 'Concrete'], required: true },
      { name: 'appliances_included', label: 'Appliances Included', type: 'checkbox', required: false },
      { name: 'backsplash', label: 'Backsplash Required', type: 'checkbox', required: false },
      { name: 'flooring', label: 'New Flooring', type: 'checkbox', required: false },
      { name: 'lighting_upgrade', label: 'Lighting Upgrade', type: 'checkbox', required: false },
      { name: 'island', label: 'Kitchen Island', type: 'select', options: ['None', 'New Island', 'Modify Existing', 'Remove Existing'], required: false },
      { name: 'plumbing_relocation', label: 'Plumbing Relocation Needed', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'bathroom_remodel',
    name: 'Bathroom Remodel',
    icon: Bath,
    color: 'cyan',
    description: 'Bathroom renovation projects',
    fields: [
      { name: 'bathroom_type', label: 'Bathroom Type', type: 'select', options: ['Full Bath', 'Half Bath', 'Master Bath', 'Guest Bath'], required: true },
      { name: 'bathroom_size', label: 'Bathroom Size (sq ft)', type: 'number', required: true },
      { name: 'shower_type', label: 'Shower Type', type: 'select', options: ['Tub/Shower Combo', 'Walk-in Shower', 'Freestanding Tub', 'Steam Shower', 'None'], required: false },
      { name: 'vanity_type', label: 'Vanity Type', type: 'select', options: ['Single Sink', 'Double Sink', 'Floating Vanity', 'Custom Built-in'], required: true },
      { name: 'tile_work', label: 'Tile Work Required', type: 'checkbox', required: false },
      { name: 'heated_floor', label: 'Heated Flooring', type: 'checkbox', required: false },
      { name: 'ventilation', label: 'Ventilation Upgrade', type: 'checkbox', required: false },
      { name: 'accessibility', label: 'ADA Accessibility Features', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'roofing',
    name: 'Roofing',
    icon: Home,
    color: 'red',
    description: 'Roof repair and replacement',
    fields: [
      { name: 'job_type', label: 'Job Type', type: 'select', options: ['Full Replacement', 'Partial Repair', 'New Installation', 'Inspection Only'], required: true },
      { name: 'roof_size', label: 'Roof Size (sq ft)', type: 'number', required: true },
      { name: 'current_material', label: 'Current Material', type: 'select', options: ['Asphalt Shingle', 'Metal', 'Tile', 'Slate', 'Flat/TPO', 'Wood Shake'], required: false },
      { name: 'new_material', label: 'New Material', type: 'select', options: ['Asphalt Shingle', 'Metal', 'Tile', 'Slate', 'Flat/TPO', 'Wood Shake'], required: true },
      { name: 'layers_remove', label: 'Layers to Remove', type: 'number', required: false },
      { name: 'gutters', label: 'Gutter Work Included', type: 'checkbox', required: false },
      { name: 'skylights', label: 'Skylights', type: 'select', options: ['None', 'Keep Existing', 'Add New', 'Remove'], required: false },
      { name: 'ventilation', label: 'Ventilation Upgrade', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'deck_patio',
    name: 'Deck & Patio',
    icon: TreePine,
    color: 'emerald',
    description: 'Outdoor living spaces',
    fields: [
      { name: 'project_type', label: 'Project Type', type: 'select', options: ['New Deck', 'Deck Replacement', 'Patio', 'Pergola', 'Screen Porch', 'Combination'], required: true },
      { name: 'size_sqft', label: 'Size (sq ft)', type: 'number', required: true },
      { name: 'material', label: 'Decking Material', type: 'select', options: ['Pressure Treated Wood', 'Cedar', 'Composite', 'PVC', 'Hardwood', 'Concrete/Pavers'], required: true },
      { name: 'levels', label: 'Number of Levels', type: 'number', required: false },
      { name: 'railings', label: 'Railing Type', type: 'select', options: ['Wood', 'Composite', 'Metal', 'Cable', 'Glass', 'None'], required: false },
      { name: 'stairs', label: 'Stairs Needed', type: 'checkbox', required: false },
      { name: 'lighting', label: 'Built-in Lighting', type: 'checkbox', required: false },
      { name: 'outdoor_kitchen', label: 'Outdoor Kitchen/Grill Area', type: 'checkbox', required: false }
    ]
  },
  {
    id: 'painting',
    name: 'Painting & Finishing',
    icon: Paintbrush,
    color: 'purple',
    description: 'Interior and exterior painting',
    fields: [
      { name: 'scope', label: 'Scope', type: 'select', options: ['Interior Only', 'Exterior Only', 'Both'], required: true },
      { name: 'sqft_walls', label: 'Wall Area (sq ft)', type: 'number', required: true },
      { name: 'rooms', label: 'Number of Rooms', type: 'number', required: false },
      { name: 'ceiling_included', label: 'Ceilings Included', type: 'checkbox', required: false },
      { name: 'trim_included', label: 'Trim/Baseboards Included', type: 'checkbox', required: false },
      { name: 'cabinet_painting', label: 'Cabinet Painting', type: 'checkbox', required: false },
      { name: 'wallpaper_removal', label: 'Wallpaper Removal', type: 'checkbox', required: false },
      { name: 'paint_grade', label: 'Paint Grade', type: 'select', options: ['Builder Grade', 'Standard', 'Premium', 'Designer'], required: false }
    ]
  }
];

// Quote Templates for Construction
const constructionQuoteTemplates = [
  {
    id: 'construction_estimate',
    name: 'Construction Estimate',
    description: 'Detailed construction project estimate',
    sections: ['Labor', 'Materials', 'Equipment', 'Permits & Fees', 'Contingency'],
    defaultItems: [
      { description: 'Demolition & Site Prep', category: 'Labor', quantity: 1, unit: 'project', rate: 2500 },
      { description: 'Framing Labor', category: 'Labor', quantity: 0, unit: 'sq ft', rate: 8 },
      { description: 'Electrical Rough-In', category: 'Labor', quantity: 1, unit: 'project', rate: 3500 },
      { description: 'Plumbing Rough-In', category: 'Labor', quantity: 1, unit: 'project', rate: 4000 },
      { description: 'Lumber & Framing Materials', category: 'Materials', quantity: 0, unit: 'lot', rate: 0 },
      { description: 'Drywall & Finishing', category: 'Materials', quantity: 0, unit: 'sheet', rate: 45 },
      { description: 'Equipment Rental', category: 'Equipment', quantity: 1, unit: 'week', rate: 500 },
      { description: 'Building Permits', category: 'Permits & Fees', quantity: 1, unit: 'project', rate: 0 },
      { description: 'Contingency (10%)', category: 'Contingency', quantity: 1, unit: 'project', rate: 0 }
    ]
  },
  {
    id: 'material_labor',
    name: 'Material & Labor Quote',
    description: 'Itemized materials and labor breakdown',
    sections: ['Materials', 'Labor', 'Subcontractors'],
    defaultItems: [
      { description: 'General Labor', category: 'Labor', quantity: 0, unit: 'hour', rate: 65 },
      { description: 'Skilled Trade Labor', category: 'Labor', quantity: 0, unit: 'hour', rate: 95 },
      { description: 'Project Manager', category: 'Labor', quantity: 0, unit: 'hour', rate: 85 },
      { description: 'Materials (itemized)', category: 'Materials', quantity: 1, unit: 'lot', rate: 0 },
      { description: 'Material Delivery', category: 'Materials', quantity: 1, unit: 'trip', rate: 150 },
      { description: 'Subcontractor - Electrical', category: 'Subcontractors', quantity: 1, unit: 'project', rate: 0 },
      { description: 'Subcontractor - Plumbing', category: 'Subcontractors', quantity: 1, unit: 'project', rate: 0 },
      { description: 'Subcontractor - HVAC', category: 'Subcontractors', quantity: 1, unit: 'project', rate: 0 }
    ]
  },
  {
    id: 'change_order',
    name: 'Change Order Form',
    description: 'Document scope changes during project',
    sections: ['Change Description', 'Cost Impact', 'Schedule Impact'],
    defaultItems: [
      { description: 'Additional Labor', category: 'Labor', quantity: 0, unit: 'hour', rate: 75 },
      { description: 'Additional Materials', category: 'Materials', quantity: 1, unit: 'lot', rate: 0 },
      { description: 'Schedule Extension', category: 'Schedule', quantity: 0, unit: 'day', rate: 0 }
    ]
  }
];

const FormTemplatesConstruction = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('projects');
  const [expandedProject, setExpandedProject] = useState(null);

  const getColorClasses = (color) => {
    const colors = {
      amber: 'bg-amber-50 text-amber-600 border-amber-200',
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
      red: 'bg-red-50 text-red-600 border-red-200',
      emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200'
    };
    return colors[color] || colors.amber;
  };

  const getFieldTypeLabel = (type) => {
    const types = {
      text: 'Text Input',
      number: 'Number',
      textarea: 'Text Area',
      select: 'Dropdown',
      checkbox: 'Checkbox',
      date: 'Date Picker'
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/settings')}
            className="shrink-0"
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-600 flex items-center justify-center">
              <Hammer size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Construction Contractor Templates</h1>
              <p className="text-slate-500">Manage project types and quote templates for construction businesses</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="projects" className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700">
              <FileText size={16} className="mr-2" />
              Project Types
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700">
              <Receipt size={16} className="mr-2" />
              Quote Templates
            </TabsTrigger>
          </TabsList>

          {/* Project Types Tab */}
          <TabsContent value="projects" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Configure the fields shown when creating construction projects
              </p>
              <Button className="bg-amber-600 hover:bg-amber-700">
                <Plus size={16} className="mr-2" />
                Add Project Type
              </Button>
            </div>

            <div className="grid gap-4">
              {constructionProjectTypes.map((projectType) => {
                const Icon = projectType.icon;
                const isExpanded = expandedProject === projectType.id;
                
                return (
                  <Card key={projectType.id} className="border-slate-200 shadow-sm">
                    <CardHeader 
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedProject(isExpanded ? null : projectType.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(projectType.color)}`}>
                            <Icon size={20} />
                          </div>
                          <div>
                            <CardTitle className="text-lg font-semibold text-slate-900">
                              {projectType.name}
                            </CardTitle>
                            <CardDescription>{projectType.description}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-500">
                            {projectType.fields.length} fields
                          </span>
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {isExpanded && (
                      <CardContent className="border-t border-slate-100 pt-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-sm font-semibold text-slate-700">Form Fields</h4>
                            <Button size="sm" variant="outline">
                              <Plus size={14} className="mr-1" />
                              Add Field
                            </Button>
                          </div>
                          
                          {projectType.fields.map((field, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                            >
                              <div className="flex items-center gap-3">
                                <GripVertical size={16} className="text-slate-400 cursor-grab" />
                                <div>
                                  <p className="font-medium text-slate-900">{field.label}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                                      {getFieldTypeLabel(field.type)}
                                    </span>
                                    {field.required && (
                                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                                        Required
                                      </span>
                                    )}
                                    {field.options && (
                                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">
                                        {field.options.length} options
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm">
                                  <Pencil size={14} />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Quote Templates Tab */}
          <TabsContent value="quotes" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Configure quote templates for construction estimates
              </p>
              <Button className="bg-amber-600 hover:bg-amber-700">
                <Plus size={16} className="mr-2" />
                Add Quote Template
              </Button>
            </div>

            <div className="grid gap-4">
              {constructionQuoteTemplates.map((template) => (
                <Card key={template.id} className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                          <Receipt size={20} className="text-green-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold text-slate-900">
                            {template.name}
                          </CardTitle>
                          <CardDescription>{template.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <Eye size={14} className="mr-1" />
                          Preview
                        </Button>
                        <Button variant="outline" size="sm">
                          <Pencil size={14} className="mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="border-t border-slate-100">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Sections</h4>
                        <div className="flex flex-wrap gap-2">
                          {template.sections.map((section, idx) => (
                            <span key={idx} className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                              {section}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Default Line Items</h4>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-slate-500">
                                <th className="pb-2">Description</th>
                                <th className="pb-2">Category</th>
                                <th className="pb-2 text-right">Unit</th>
                                <th className="pb-2 text-right">Rate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {template.defaultItems.map((item, idx) => (
                                <tr key={idx} className="border-t border-slate-200">
                                  <td className="py-2 text-slate-900">{item.description}</td>
                                  <td className="py-2">
                                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                                      {item.category}
                                    </span>
                                  </td>
                                  <td className="py-2 text-right text-slate-600">{item.unit}</td>
                                  <td className="py-2 text-right text-slate-900">
                                    {item.rate > 0 ? `$${item.rate.toLocaleString()}` : 'TBD'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Construction-specific Info */}
            <Card className="border-amber-200 shadow-sm bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <DollarSign size={20} className="text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-slate-900">Construction Quote Tips</h4>
                    <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
                      <li>Always include a contingency line item (typically 10-15%)</li>
                      <li>Separate labor and materials for transparency</li>
                      <li>Include permit costs as a separate line item</li>
                      <li>Use "TBD" rates for items that require site assessment</li>
                      <li>Add change order clauses to the quote terms</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FormTemplatesConstruction;
